import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
} from "@mui/material";
import { useState } from "react";
import Button from "../../../../../../components/ui/Button";
import Text from "../../../../../../components/ui/Text";
import { GAME_PAYMENT_MODE } from "../../../../../../constants/game";
import { useAuth } from "../../../../../../contexts/AuthContext";
import { useToast } from "../../../../../../contexts/ToastContext";
import apiService from "../../../../../../services/api";
import { AddPlayerDialog } from "../../AddPlayerDialog";

export const PlayerMenu = ({
  isCurrentUserHost,
  isAddedByCurrentUser,
  isPlayerPaid,
  isPlayerInvited,
  playerMenuAnchor,
  handlePlayerMenuClose,
  selectedPlayerId,
  players,
  gameId,
  gamePaymentMode,
  fetchPlayers,
  fetchGameDetails,
}) => {
  const { showToast } = useToast();
  const { currentUser, fetchUserProfile } = useAuth();
  const [isRemovePlayerDialogOpen, setIsRemovePlayerDialogOpen] =
    useState(false);
  const [isEditPlayerDialogOpen, setIsEditPlayerDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(false);
  const [isShareHostAccessModalOpen, setIsShareHostAccessModalOpen] =
    useState(false);
  // const [isMarkAsPaidDialogOpen, setIsMarkAsPaidDialogOpen] = useState(false);

  const handleConfirmShareHostAccess = () => {
    showToast("This feature is coming soon!", "info");
    setIsShareHostAccessModalOpen(false);
  };

  const handleConfirmChangePaidStatus = async () => {
    handlePlayerMenuClose();
    try {
      const response = await apiService.changePlayerPaidStatus(
        gameId,
        selectedPlayerId,
        { paid: !isPlayerPaid },
      );
      if (response.success) {
        showToast(response.message, "success");
        fetchPlayers();
      }
    } catch (error) {
      showToast(error.error, "error");
    }
  };

  const handleConfirmRemovePlayer = async () => {
    try {
      const response = isCurrentUserHost
        ? await apiService.removePlayerFromGame(gameId, selectedPlayerId)
        : await apiService.removeUnregisteredPlayer(gameId, selectedPlayerId);
      if (response.success) {
        showToast(response.message, "success");
        fetchPlayers();
        fetchGameDetails();
        if (!isCurrentUserHost) {
          fetchUserProfile(currentUser?.accessToken);
        }
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      handlePlayerMenuClose();
      setIsRemovePlayerDialogOpen(false);
    }
  };

  const handleSubmitEditPlayer = async (updatedPlayer) => {
    setEditingPlayer(true);
    try {
      const response = await apiService.updateUnregisteredPlayer(
        gameId,
        selectedPlayerId,
        updatedPlayer,
      );
      if (response.success) {
        await fetchPlayers();
        showToast(response.message, "success");
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setEditingPlayer(false);
      setIsEditPlayerDialogOpen(false);
      handlePlayerMenuClose();
    }
  };

  return (
    <>
      <Menu
        anchorEl={playerMenuAnchor}
        open={Boolean(playerMenuAnchor)}
        onClose={handlePlayerMenuClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              minWidth: "160px",
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {selectedPlayerId && isCurrentUserHost && !isPlayerInvited && (
          <MenuItem
            onClick={() => setIsShareHostAccessModalOpen(true)}
            sx={{
              fontSize: "14px",
              py: 0.5,
              px: 2,
            }}
          >
            Share host access
          </MenuItem>
        )}
        {selectedPlayerId &&
          isCurrentUserHost &&
          gamePaymentMode === GAME_PAYMENT_MODE.SELF &&
          !isPlayerInvited && (
            <MenuItem
              onClick={handleConfirmChangePaidStatus}
              sx={{
                fontSize: "14px",
                py: 0.5,
                px: 2,
              }}
            >
              {isPlayerPaid ? "Unmark as paid" : "Mark as paid"}
            </MenuItem>
          )}
        {selectedPlayerId && isAddedByCurrentUser && (
          <MenuItem
            onClick={() => {
              setIsEditPlayerDialogOpen(true);
            }}
            sx={{
              fontSize: "14px",
              py: 0.5,
              px: 2,
            }}
          >
            Edit player
          </MenuItem>
        )}
        {selectedPlayerId && (
          <MenuItem
            onClick={() => {
              setIsRemovePlayerDialogOpen(true);
            }}
            sx={{
              fontSize: "14px",
              py: 0.5,
              px: 2,
              color: "#FF4D4F",
            }}
          >
            Remove player
          </MenuItem>
        )}
      </Menu>

      {/* Share Host Access Confirmation Dialog */}
      <Dialog
        open={isShareHostAccessModalOpen}
        onClose={() => setIsShareHostAccessModalOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              mx: 2,
              width: "310px",
            },
          },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Text variant="pageHeader" sx={{ mb: 0, fontSize: "20px" }}>
            Are you sure you want to share host access?
          </Text>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Text variant="paragraph" sx={{ fontSize: "16px", mb: 2 }}>
            This player will be able to:
          </Text>
          <Box sx={{ ml: 0, my: 2 }}>
            <Text
              variant="paragraph"
              sx={{ fontSize: "16px", display: "block", mb: 0.5 }}
            >
              • Edit the game
            </Text>
            <Text
              variant="paragraph"
              sx={{ fontSize: "16px", display: "block", mb: 0.5 }}
            >
              • Remove players
            </Text>
            <Text
              variant="paragraph"
              sx={{ fontSize: "16px", display: "block" }}
            >
              • Create and manage teams
            </Text>
          </Box>
          <Text variant="paragraph" sx={{ fontSize: "16px" }}>
            Once shared, we'll send an email to let them know.
          </Text>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 1,
            gap: 2,
            display: "flex",
          }}
        >
          <Button
            variant="secondary"
            onClick={() => setIsShareHostAccessModalOpen(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmShareHostAccess}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Paid Confirmation Dialog */}
      {/* <Dialog
        open={isMarkAsPaidDialogOpen}
        onClose={() => setIsMarkAsPaidDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: "12px", m: 2, width: "310px" } },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Text variant="pageHeader" sx={{ mb: 0, fontSize: "20px" }}>
            {isPlayerPaid ? "Unmark player as paid" : "Mark player as paid"}?
          </Text>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Text variant="paragraph" sx={{ fontSize: "16px", mb: 2 }}>
            {isPlayerPaid
              ? "Are you sure you want to unmark this player as paid?"
              : "Are you sure you want to mark this player as paid?"}
          </Text>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 1,
            gap: 2,
            display: "flex",
          }}
        >
          <Button
            variant="secondary"
            onClick={() => setIsMarkAsPaidDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmChangePaidStatus}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog> */}

      {/* Remove Player Confirmation Dialog */}
      <Dialog
        open={isRemovePlayerDialogOpen}
        onClose={() => setIsRemovePlayerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: "12px", m: 2, width: "310px" } },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Text variant="pageHeader" sx={{ mb: 0, fontSize: "20px" }}>
            Remove player
          </Text>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Text variant="paragraph" sx={{ fontSize: "16px" }}>
            Are you sure you want to remove this player?
          </Text>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 1,
            gap: 2,
            display: "flex",
          }}
        >
          <Button
            variant="secondary"
            onClick={() => setIsRemovePlayerDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemovePlayer}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Player Modal */}
      <AddPlayerDialog
        isOpen={isEditPlayerDialogOpen}
        onClose={() => {
          setIsEditPlayerDialogOpen(false);
        }}
        onSubmit={handleSubmitEditPlayer}
        players={players}
        loading={editingPlayer}
        editMode
        selectedPlayerId={selectedPlayerId}
      />
    </>
  );
};
